import type { MbcBrandContext } from "@nlc/shared-types";

import { useNavigate, useRouter } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { BriefQualityCard } from "@/features/request/mbc-review/components/BriefQualityCard";
import { isTeamUser } from "@/features/requests/utils/roleUtils";
import { useUser } from "@/hooks/useUser";

import type { MbcSessionIds } from "../lib/session-ids";

import { useBriefMemory } from "../hooks/useBriefMemory";
import { useMBCChat } from "../hooks/useMBCChat";
import { discardSession } from "../lib/draft-api";
import { buildGatePreamble, isStartMessage } from "../lib/gate-preamble";
import { DiscardBriefDialog } from "./DiscardBriefDialog";
import { MBCChatHeader } from "./MBCChatHeader";
import { MBCChatPanel } from "./MBCChatPanel";
import { UploadAssetsModal } from "./upload-assets/UploadAssetsModal";
import { UploadedFilesPanel } from "./UploadedFilesPanel";

type MBCConversationProps = {
  brand: MbcBrandContext | null;
  designType?: number;
  projectId?: number;
  resume?: MbcSessionIds;
};

export function MBCConversation({
  brand,
  designType,
  projectId,
  resume,
}: MBCConversationProps) {
  const navigate = useNavigate();
  const router = useRouter();
  const { data: userData } = useUser();
  const isTeamMember = isTeamUser(userData?.user?.roleName);

  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [isDiscardOpen, setIsDiscardOpen] = useState(false);
  const [isDiscarding, setIsDiscarding] = useState(false);

  const {
    error,
    isResumeEmpty,
    isStreaming,
    messages,
    refreshSignal,
    resourceId,
    sendUserMessage,
    status,
    threadId,
  } = useMBCChat({ enabled: true, projectId, resume });

  // Before the first send, no request exists yet (T8) so resourceId/threadId
  // are undefined — useBriefMemory requires strings, and an empty id just
  // fetches nothing meaningful until the real ids land on the next render.
  const briefMemory = useBriefMemory({
    refreshSignal,
    resourceId: resourceId ?? "",
    threadId: threadId ?? "",
  });

  const variationCount = Array.isArray(briefMemory.memory?.variations)
    ? briefMemory.memory.variations.length
    : 0;

  const canReview = briefMemory.quality?.canSubmit === true;

  // Publish the session ids to the URL so closing/refreshing the page can come
  // back to this thread instead of silently starting a new one. `replace` keeps
  // it out of the history stack; the ref keeps it to a single write per thread.
  const syncedThreadId = useRef<null | string>(null);

  useEffect(() => {
    // Resume mode already came from the URL — nothing to write back.
    if (resume) return;
    if (!threadId || !resourceId || syncedThreadId.current === threadId) return;
    syncedThreadId.current = threadId;

    void navigate({
      replace: true,
      search: {
        brandId: brand?.id,
        designType,
        ["project-id"]: projectId,
        resourceId,
        threadId,
      },
      to: "/requests/mbc",
    });
  }, [
    brand?.id,
    designType,
    navigate,
    projectId,
    resume,
    resourceId,
    threadId,
  ]);

  const handleReview = useCallback(() => {
    void navigate({
      search: {
        brandId: brand?.id,
        designType,
        ["project-id"]: projectId,
        resourceId,
        threadId,
      },
      to: "/requests/mbc-review",
    });
  }, [brand?.id, designType, navigate, projectId, resourceId, threadId]);

  const handleBack = useCallback(() => {
    router.history.back();
  }, [router]);

  // Switch to the Classic Brief Form: discard the MBC session, then redirect.
  const confirmSwitchToClassic = useCallback(async () => {
    setIsDiscarding(true);
    try {
      if (threadId) await discardSession(threadId);

      // `replace` drops the /requests/mbc?threadId=… entry this session wrote,
      // so Back can't land on a thread that no longer exists.
      void navigate({
        replace: true,
        search: {
          designType,
          ["project-id"]: projectId,
          ["request-type"]: "graphic",
        },
        to: "/requests/create",
      });
    } finally {
      setIsDiscarding(false);
      setIsDiscardOpen(false);
    }
  }, [designType, navigate, projectId, threadId]);

  // Fresh conversation: show the brand-step choices as the opening chat entries.
  // Resume: show the rehydrated transcript as-is. Either way, hide the generic
  // kickoff message that triggers a fresh conversation.
  const displayMessages = useMemo(() => {
    const history = messages.filter((message) => !isStartMessage(message));
    // A resume that found nothing is really a fresh conversation, so it gets
    // the preamble too — otherwise the chat opens mid-conversation.
    const isFresh = !resume || isResumeEmpty;

    return isFresh ? [...buildGatePreamble(brand), ...history] : history;
  }, [brand, isResumeEmpty, messages, resume]);

  return (
    <>
      <MBCChatHeader
        isTeamMember={isTeamMember}
        onBack={handleBack}
        onSwitchToClassic={() => setIsDiscardOpen(true)}
      />

      <div className="flex min-h-0 flex-1 gap-3xl p-3xl">
        <MBCChatPanel
          canReview={canReview}
          isStreaming={isStreaming}
          messages={displayMessages}
          onAttach={() => setIsUploadOpen(true)}
          onReview={handleReview}
          onSelectChoice={sendUserMessage}
          onSend={sendUserMessage}
          status={status}
          upload={{
            memory: briefMemory.memory,
            onUploaded: () => void briefMemory.refetch(),
            resourceId,
            threadId,
          }}
        />

        <div className="flex w-full max-w-[320px] shrink-0 flex-col gap-xl">
          <BriefQualityCard quality={briefMemory.quality} />

          <UploadedFilesPanel
            memory={briefMemory.memory}
            onAddAssets={() => setIsUploadOpen(true)}
          />
        </div>
      </div>

      {error ? (
        <p className="px-3xl pb-lg text-sm text-text-error-primary">
          Something went wrong. Please refresh and try again.
        </p>
      ) : null}

      <UploadAssetsModal
        isOpen={isUploadOpen}
        memory={briefMemory.memory}
        onOpenChange={setIsUploadOpen}
        resourceId={resourceId}
        threadId={threadId}
        variationCount={variationCount}
      />

      <DiscardBriefDialog
        isDiscarding={isDiscarding}
        isOpen={isDiscardOpen}
        onConfirm={() => void confirmSwitchToClassic()}
        onOpenChange={setIsDiscardOpen}
      />
    </>
  );
}
